import React, { useState } from 'react';
import { useTrial } from '../contexts/TrialContext';
import ProModeSelector from './ProModeSelector';
import '../styles/NewGamePanel.css';

const VARIANTS = [
  { id: "standard", name: "Standard Chess", free: true },
  { id: "fight_to_be_king", name: "Fight to be King", free: true },
  { id: "kings_court_2dukes", name: "Chess Kingdom (Two Dukes)", free: false },
  { id: "players_choice", name: "Player's Choice", free: false },
  { id: "instant_king", name: "Instant King", free: false },
  { id: "pawns_wild", name: "King's Court: Pawn's Wild", free: false },
  { id: "handicap_mode", name: "Standard Chess vs Royal Chess Kingdom", free: false }
];

const NewGamePanel = ({ onNewGame, onClose, onOpenSettings }) => {
  const { hasActiveAccess } = useTrial();
  const [selectedVariant, setSelectedVariant] = useState('fight_to_be_king');
  const [whiteEmbodiment, setWhiteEmbodiment] = useState('instant_king'); // For Player's Choice variant
  const [blackEmbodiment, setBlackEmbodiment] = useState('instant_king'); // For Player's Choice variant
  const [vsAI, setVsAI] = useState(false);
  const [aiDifficulty, setAiDifficulty] = useState(2);
  // Fight to be King → Professional sub-mode: 'off' | 'pro_1' | 'pro_2'
  const [ftbkProMode, setFtbkProMode] = useState('off');
  // Asymmetric Pro mode: allow each side to have its own Pro setting ('off'|'pro_1'|'pro_2')
  const [ftbkAsymmetric, setFtbkAsymmetric] = useState(false);
  const [ftbkWhitePro, setFtbkWhitePro] = useState('pro_1');
  const [ftbkBlackPro, setFtbkBlackPro] = useState('pro_2');
  
  // For Handicap Mode
  const [dukeSide, setDukeSide] = useState('white'); // Which player gets the Duke
  const [dukeEmbodiment, setDukeEmbodiment] = useState('instant_king'); // Duke embodiment for handicap mode

  const handleStartGame = () => {
    const settings = {
      variant: selectedVariant,
      vs_ai: vsAI,
      ai_difficulty: aiDifficulty
    };
    
    // For Player's Choice, include the chosen embodiments (each player chooses independently)
    if (selectedVariant === 'players_choice') {
      settings.white_embodiment = whiteEmbodiment;
      settings.black_embodiment = blackEmbodiment;
    } else if (selectedVariant === 'kings_court_2dukes') {
      // Kingdom Chess: Dukes have pre-assigned embodiments by position
      // Frontend doesn't need to send embodiment for this variant
      settings.white_embodiment = 'instant_king';  // Default, not used
      settings.black_embodiment = 'instant_king';  // Default, not used
    } else if (selectedVariant === 'instant_king' || selectedVariant === 'fight_to_be_king') {
      // Single Duke variants: embodiment is fixed by variant type
      if (selectedVariant === 'fight_to_be_king' && ftbkAsymmetric) {
        // Per-side Pro mode: each side picks classic / pro_1 / pro_2 independently
        const toEmb = (v) => v === 'pro_1' ? 'fight_to_be_king_pro_1'
                          : v === 'pro_2' ? 'fight_to_be_king_pro_2'
                          : 'fight_to_be_king';
        settings.white_embodiment = toEmb(ftbkWhitePro);
        settings.black_embodiment = toEmb(ftbkBlackPro);
      } else if (selectedVariant === 'fight_to_be_king' && ftbkProMode !== 'off') {
        const proEmb = ftbkProMode === 'pro_1' ? 'fight_to_be_king_pro_1' : 'fight_to_be_king_pro_2';
        settings.white_embodiment = proEmb;
        settings.black_embodiment = proEmb;
      } else {
        settings.white_embodiment = selectedVariant;
        settings.black_embodiment = selectedVariant;
      }
    } else if (selectedVariant === 'pawns_wild') {
      // Pawn's Wild: Standard board, pawns can promote to Dukes
      // When promoted to Duke, use instant_king embodiment by default
      settings.white_embodiment = 'instant_king';
      settings.black_embodiment = 'instant_king';
    } else if (selectedVariant === 'handicap_mode') {
      // Handicap Mode: One player gets Duke, other gets Bishop
      settings.duke_side = dukeSide; // 'white' or 'black'
      settings.duke_embodiment = dukeEmbodiment; // 'instant_king' or 'fight_to_be_king'
      // Set embodiments based on which side has the Duke
      if (dukeSide === 'white') {
        settings.white_embodiment = dukeEmbodiment;
        settings.black_embodiment = 'standard'; // Bishop side
      } else {
        settings.white_embodiment = 'standard'; // Bishop side
        settings.black_embodiment = dukeEmbodiment;
      }
    }
    
    onNewGame(settings);
  };

  return (
    <div className="new-game-panel-overlay" onClick={onClose}>
      <div className="new-game-panel" onClick={(e) => e.stopPropagation()}>
        <div className="panel-header">
          <h2>New Game</h2>
        </div>
        
        <div className="game-settings">
          
          {/* Game Variant Selector */}
          <div className="setting-section">
            <h3>Game Variant</h3>
            <select
              className="variant-select"
              value={selectedVariant}
              onChange={(e) => setSelectedVariant(e.target.value)}
            >
              {VARIANTS.map(v => {
                const isLocked = !v.free && !hasActiveAccess();
                return (
                  <option key={v.id} value={v.id} disabled={isLocked}>
                    {isLocked ? `🔒 ${v.name} (Premium)` : v.name}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Professional Mode toggle for Fight to be King */}
          {selectedVariant === 'fight_to_be_king' && (
            <ProModeSelector
              value={ftbkProMode}
              onChange={setFtbkProMode}
              asymmetric={ftbkAsymmetric}
              onAsymmetricChange={setFtbkAsymmetric}
              whitePro={ftbkWhitePro}
              blackPro={ftbkBlackPro}
              onWhiteProChange={setFtbkWhitePro}
              onBlackProChange={setFtbkBlackPro}
            />
          )}

          {/* Show embodiment selection only for Player's Choice */}
          {selectedVariant === 'players_choice' && (
            <>
              <div className="setting-section">
                <h3>White Player Embodiment</h3>
                <div className="embodiment-icons">
                  <button 
                    className={`embodiment-icon-button ${whiteEmbodiment === 'instant_king' ? 'active' : ''}`}
                    onClick={() => setWhiteEmbodiment('instant_king')}
                    title="Instant King - Transform immediately when King captured"
                  >
                    <span className="icon">⚡</span>
                    <span className="label">Instant King</span>
                  </button>
                  <button 
                    className={`embodiment-icon-button ${whiteEmbodiment === 'fight_to_be_king' ? 'active' : ''}`}
                    onClick={() => setWhiteEmbodiment('fight_to_be_king')}
                    title="Fight to be King - Must reach baseline to transform"
                  >
                    <span className="icon">⚔️</span>
                    <span className="label">Fight to be King</span>
                  </button>
                </div>
              </div>
              
              <div className="setting-section">
                <h3>Black Player Embodiment</h3>
                <div className="embodiment-icons">
                  <button 
                    className={`embodiment-icon-button ${blackEmbodiment === 'instant_king' ? 'active' : ''}`}
                    onClick={() => setBlackEmbodiment('instant_king')}
                    title="Instant King - Transform immediately when King captured"
                  >
                    <span className="icon">⚡</span>
                    <span className="label">Instant King</span>
                  </button>
                  <button 
                    className={`embodiment-icon-button ${blackEmbodiment === 'fight_to_be_king' ? 'active' : ''}`}
                    onClick={() => setBlackEmbodiment('fight_to_be_king')}
                    title="Fight to be King - Must reach baseline to transform"
                  >
                    <span className="icon">⚔️</span>
                    <span className="label">Fight to be King</span>
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Show handicap mode settings only for Handicap Mode */}
          {selectedVariant === 'handicap_mode' && (
            <>
              <div className="setting-section">
                <h3>Who Gets the Duke?</h3>
                <p className="embodiment-note">Other player gets standard Bishop</p>
                <div className="embodiment-icons">
                  <button 
                    className={`embodiment-icon-button ${dukeSide === 'white' ? 'active' : ''}`}
                    onClick={() => setDukeSide('white')}
                    title="White player gets Duke"
                  >
                    <span className="icon">⚪</span>
                    <span className="label">White Gets Duke</span>
                  </button>
                  <button 
                    className={`embodiment-icon-button ${dukeSide === 'black' ? 'active' : ''}`}
                    onClick={() => setDukeSide('black')}
                    title="Black player gets Duke"
                  >
                    <span className="icon">⚫</span>
                    <span className="label">Black Gets Duke</span>
                  </button>
                </div>
              </div>
              
              <div className="setting-section">
                <h3>Duke Embodiment</h3>
                <p className="embodiment-note">Choose which Duke type the Royal Chess player gets</p>
                <div className="embodiment-icons">
                  <button 
                    className={`embodiment-icon-button ${dukeEmbodiment === 'instant_king' ? 'active' : ''}`}
                    onClick={() => setDukeEmbodiment('instant_king')}
                    title="Instant King - Transform immediately when King captured"
                  >
                    <span className="icon">⚡</span>
                    <span className="label">Instant King</span>
                  </button>
                  <button 
                    className={`embodiment-icon-button ${dukeEmbodiment === 'fight_to_be_king' ? 'active' : ''}`}
                    onClick={() => setDukeEmbodiment('fight_to_be_king')}
                    title="Fight to be King - Must reach baseline to transform"
                  >
                    <span className="icon">⚔️</span>
                    <span className="label">Fight to be King</span>
                  </button>
                </div>
              </div>
            </>
          )}

          <div className="setting-section">
            <h3>Opponent</h3>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={vsAI}
                onChange={(e) => setVsAI(e.target.checked)}
              />
              <span>Play against AI</span>
            </label>
            
            {vsAI && (
              <div className="difficulty-section">
                <label>AI Difficulty:</label>
                <select 
                  value={aiDifficulty} 
                  onChange={(e) => setAiDifficulty(parseInt(e.target.value))}
                  className="difficulty-select"
                >
                  <option value={1}>Easy</option>
                  <option value={2}>Medium</option>
                  <option value={3}>Hard</option>
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="panel-actions">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleStartGame} data-testid="start-game-btn">
            Start Game
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewGamePanel;
